import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useState } from "react";
import styles from "./Login.module.css"; // Reutilizamos los estilos del login

const schema = yup.object().shape({
  fullName: yup.string().required("El nombre completo es requerido"),
  email: yup.string().email("Email inválido").required("El email es requerido"),
  password: yup
    .string()
    .required("La contraseña es requerida")
    .min(8, "La contraseña debe tener al menos 8 caracteres")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "La contraseña debe contener al menos una mayúscula, una minúscula y un número"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Las contraseñas deben coincidir")
    .required("Confirma tu contraseña"),
});

function Signup() {
  const { signup, loading, error } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      await signup(data.email, data.password, data.fullName);
      navigate("/dashboard");
    } catch (err) {
      console.error("Error en registro:", err);
    } finally {
      setIsSubmitting(false);
    }
  };
  

  return (
    <div className="min-h-screen flex items-center justify-center pt-12 bg-[#1A1A1A] pb-8 px-4 sm:px-6 lg:px-8 text-white">
      <div className="max-w-md w-full space-y-3">
        <div>
          <img src="/wemax-logo.png" alt="Wemax Logo" className="mx-auto h-14 mb-4" />
          <h2 className="mt-2 text-center text-3xl font-medium text-white">
            Crea tu cuenta en Wemax
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            Regístrate para comenzar a usar la suite de Wemax
          </p>
        </div>
        
        {/* Formulario de registro tradicional */}
        <form className={`mt-8 space-y-4 ${styles.formContainer}`} onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-1">
                Nombre completo
              </label>
              <input
                {...register("fullName")}
                id="fullName"
                type="text"
                autoComplete="name"
                className={`appearance-none relative block w-full px-3 py-3 rounded-full sm:text-sm bg-[#1e1e1e] border border-gray-700 placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-[#ff1b6d] focus:border-[#ff1b6d] ${styles.input}`}
                placeholder="Ingresa tu nombre completo"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-400">{errors.fullName.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Correo electrónico
              </label>
              <input
                {...register("email")}
                id="email"
                type="email"
                autoComplete="email"
                className={`appearance-none relative block w-full px-3 py-3 rounded-full sm:text-sm bg-[#1e1e1e] border border-gray-700 placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-[#ff1b6d] focus:border-[#ff1b6d] ${styles.input}`}
                placeholder="Ingresa tu correo electrónico"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Contraseña
              </label>
              <input
                {...register("password")}
                id="password"
                type="password"
                autoComplete="new-password"
                className={`appearance-none relative block w-full px-3 py-3 rounded-full sm:text-sm bg-[#1e1e1e] border border-gray-700 placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-[#ff1b6d] focus:border-[#ff1b6d] ${styles.input}`}
                placeholder="Crea una contraseña"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Confirmar contraseña
              </label>
              <input
                {...register("confirmPassword")}
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                className={`appearance-none relative block w-full px-3 py-3 rounded-full sm:text-sm bg-[#1e1e1e] border border-gray-700 placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-[#ff1b6d] focus:border-[#ff1b6d] ${styles.input}`}
                placeholder="Confirma tu contraseña"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-400">
                    Error de registro
                  </h3>
                  <div className="mt-2 text-sm text-red-300">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              className="h-4 w-4 text-[#ff1b6d] focus:ring-[#ff1b6d] border-gray-700 rounded bg-[#1e1e1e]"
              required
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-gray-300">
              Acepto los{" "}
              <Link to="/terms" className="font-medium text-[#ff1b6d] hover:text-[#ff1b6d]/90">
                términos y condiciones
              </Link>
            </label>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || isSubmitting}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-full text-white bg-[#ff1b6d] hover:bg-[#ff1b6d]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff1b6d] ${styles.button}`}
            >
              {(loading || isSubmitting) ? (
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <svg className={`h-5 w-5 text-white ${styles.spinner}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              ) : (
                "Crear Cuenta"
              )}
            </button>
          </div>
        </form>
        
        {/* Enlace de inicio de sesión */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-400">
            ¿Ya tienes una cuenta?{" "}
            <Link to="/login" className="font-medium text-[#ff1b6d] hover:text-[#ff1b6d]/90">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;

